import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Card } from '../../api/client';
import { useBoardStore } from '../../stores/boardStore';
import { useToastStore } from '../../stores/toastStore';

const COLOR_LABEL_OPTIONS = [
  null,
  '#EF4444',
  '#F59E0B',
  '#10B981',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
];

interface CardContextMenuProps {
    card: Card;
    x: number;
    y: number;
    onClose: () => void;
}

export function CardContextMenu({ card, x, y, onClose }: CardContextMenuProps) {
    const { selectCard, deleteCard, duplicateCard, updateCard, tags, createTag, addTagToCard } = useBoardStore();
    const [showTagInput, setShowTagInput] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [showColors, setShowColors] = useState(false);

    const handleOpen = (e: React.MouseEvent) => {
        e.stopPropagation();
        selectCard(card.id);
        onClose();
    };

    const handleDuplicate = async (e: React.MouseEvent) => {
        e.stopPropagation();
        await duplicateCard(card.id);
        useToastStore.getState().addToast({ type: 'success', message: 'Card duplicated' });
        onClose();
    };

    const handleTagSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!tagInput.trim()) return;

        const tagNames = tagInput
            .split(',')
            .map(t => t.trim().replace(/^#/, ''))
            .filter(t => t.length > 0);

        try {
            for (const name of tagNames) {
                let tag = tags.find(t => t.name.toLowerCase() === name.toLowerCase());
                if (!tag) {
                    tag = await createTag({ name, color: '#E5E7EB' });
                }
                const hasTag = card.tags.some(t => t.id === tag!.id);
                if (!hasTag) {
                    await addTagToCard(card.id, tag!.id);
                }
            }
            useToastStore.getState().addToast({ type: 'success', message: `Tags added` });
        } catch (error) {
            useToastStore.getState().addToast({ type: 'error', message: 'Failed to add some tags' });
        }
        onClose();
    };

    const handleColorLabel = async (color: string | null) => {
        try {
            await updateCard(card.id, { color_label: color });
        } catch {
            useToastStore.getState().addToast({ type: 'error', message: 'Failed to set color' });
        }
        onClose();
    };

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        await deleteCard(card.id);
        onClose();
    };

    const handleCopyId = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(card.id);
        useToastStore.getState().addToast({ type: 'info', message: 'Card ID copied' });
        onClose();
    };

    // Ensure menu stays within viewport
    const menuWidth = 160;
    const menuHeight = 200; // Increased height for more items
    const adjustedX = Math.min(x, window.innerWidth - menuWidth - 10);
    const adjustedY = Math.min(y, window.innerHeight - menuHeight - 10);

    return createPortal(
        <>
            <div className="fixed inset-0 z-[9998]" onClick={onClose} />
            <div
                className="fixed z-[9999] bg-white rounded-xl shadow-xl border border-gray-200 py-1 min-w-[200px] animate-in fade-in zoom-in duration-100"
                style={{ top: adjustedY, left: adjustedX }}
                onClick={(e) => e.stopPropagation()}
            >
                <button onClick={handleOpen} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    Open
                </button>

                <button onClick={handleDuplicate} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                    Duplicate
                </button>

                <button onClick={() => setShowTagInput(!showTagInput)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                    Add Tags
                </button>

                {showTagInput && (
                    <form onSubmit={handleTagSubmit} className="px-3 pb-2">
                        <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            placeholder="tag1, tag2..."
                            autoFocus
                            className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                        />
                    </form>
                )}

                <button onClick={() => setShowColors(!showColors)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>
                    Color Label
                </button>

                {showColors && (
                    <div className="flex gap-1.5 px-3 pb-2">
                        {COLOR_LABEL_OPTIONS.map((color, i) => (
                            <button
                                key={i}
                                onClick={() => handleColorLabel(color)}
                                className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${
                                    card.color_label === color ? 'border-slate-700' : 'border-gray-200'
                                } ${!color ? 'bg-white' : ''}`}
                                style={color ? { backgroundColor: color } : undefined}
                            >
                                {!color && (
                                    <svg className="w-full h-full text-gray-400 p-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                <button onClick={handleCopyId} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m-7 10h7m-7-5h7" /></svg>
                    Copy ID
                </button>

                <div className="h-px bg-gray-100 my-1" />

                <button onClick={handleDelete} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    Delete
                </button>
            </div>
        </>,
        document.body
    );
}
